import { DateTime } from "luxon"
import { SubscriptionCount, SubscriptionDetails } from "../../types"
import { getMonth } from "."

export const getSubscriberDisplayName = (
  subscription:
    | Pick<SubscriptionDetails, "first_name" | "last_name">
    | undefined,
  subscriptionId: string
): string => {
  const first = subscription?.first_name ?? ""
  const last = subscription?.last_name ?? ""
  return `${first} ${last}`.trim() || `Subscription #${subscriptionId}`
}

export const getSubscriberInitials = (
  subscription:
    | Pick<SubscriptionDetails, "first_name" | "last_name">
    | undefined
): string => {
  const first = subscription?.first_name ?? ""
  const last = subscription?.last_name ?? ""
  if (!first && !last) return "?"
  return `${first[0] ?? ""}${last[0] ?? ""}`
}

export interface MonthlySubscriptionCount {
  month: string
  year: string
  activeCount: number
  disabledCount: number
  deletedCount: number
  totalCount: number
}

// Helper to aggregate per-year-month
const aggregate = (data: SubscriptionCount[]): Record<string, number> =>
  data.reduce<Record<string, number>>((acc, item) => {
    const date = DateTime.fromISO(item.period_start)
    if (!date.isValid) return acc

    const key = `${date.year}-${date.month}` // e.g. "2025-9"
    acc[key] = (acc[key] || 0) + item.subscription_count
    return acc
  }, {})

/**
 * Groups daily subscription count data (active/disabled/deleted) into monthly totals by year.
 * Includes both current and previous year if in range.
 */
export const groupSubscriptionCountByMonth = (
  activeSubscriptionCounts: SubscriptionCount[],
  disabledSubscriptionCounts: SubscriptionCount[],
  deletedSubscriptionCounts: SubscriptionCount[]
): MonthlySubscriptionCount[] => {
  const activeTotals = aggregate(activeSubscriptionCounts)
  const disabledTotals = aggregate(disabledSubscriptionCounts)
  const deletedTotals = aggregate(deletedSubscriptionCounts)

  // Get all unique year-month keys across all three datasets
  const uniqueKeys = new Set([
    ...Object.keys(activeTotals),
    ...Object.keys(disabledTotals),
    ...Object.keys(deletedTotals)
  ])

  // Convert to array and sort by year then month
  const sortedKeys = Array.from(uniqueKeys).sort((a, b) => {
    const [yearA, monthA] = a.split("-").map(Number)
    const [yearB, monthB] = b.split("-").map(Number)
    return yearA === yearB ? monthA - monthB : yearA - yearB
  })

  // Build final array only for months that have data
  const result: MonthlySubscriptionCount[] = sortedKeys.map(key => {
    const [year, month] = key.split("-").map(Number)
    const monthName = getMonth(year, month)
    const activeCount = activeTotals[key] || 0
    const disabledCount = disabledTotals[key] || 0
    const deletedCount = deletedTotals[key] || 0

    return {
      month: monthName,
      year: year.toString(),
      activeCount,
      disabledCount,
      deletedCount,
      totalCount: activeCount + disabledCount + deletedCount
    }
  })

  return result
}
